'use client';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useSchoolProfile } from '@hooks/useSchoolProfile';
import Email from '@icons/Email.svg';
import Location from '@icons/Location.svg';
import Phone from '@icons/Phone.svg';
import Website from '@icons/Website.svg';
import { School } from 'types/collections';
import { ROLES } from 'types/roles';

import { Header, PageContent, PageGrid, SectionBigGap } from '@components/Common';
import { LoadingErrorHandler } from '@components/LoadingErrorHandler';
import { TitleContainer } from '@components/Modal';
import { ProfileActions, ProfileInfoBlock } from '@components/Profile/components';
import { ProfileInfoTitle, TitleInfoContainer } from '@components/Profile/styles';
import { Text } from '@components/TextGroups';

import { formatAddress } from '@utils/helpers';

/**
 * School Profile component displays the school's profile, contact information and
 * allows them to reset password and send request to delete their account.
 */
export const SchoolProfile: React.FC = () => {
  const { t } = useTranslation('common');
  const { schoolProfile, requestAccountDeletion, updateProfile, isPending, isError } =
    useSchoolProfile();

  return (
    <PageGrid className="full-height" $marginTopLarge={false}>
      <PageContent>
        <LoadingErrorHandler isLoading={isPending} isError={isError}>
          <Header>{t('menu.profile')}</Header>
          <SectionBigGap>
            <TitleInfoContainer>
              <TitleContainer>
                <ProfileInfoTitle>
                  {schoolProfile?.primary_education?.vestigingsnaam || ''}
                </ProfileInfoTitle>
                <Text>{schoolProfile?.school_vestigingscode}</Text>
              </TitleContainer>
              <ProfileInfoBlock
                label={t('address')}
                value={
                  schoolProfile?.primary_education && formatAddress(schoolProfile.primary_education)
                }
                icon={<Location />}
              />
              <ProfileInfoBlock
                label={t('phone')}
                value={schoolProfile?.primary_education?.telefoonnummer}
                icon={<Phone />}
              />
              <ProfileInfoBlock
                label={t('website')}
                value={schoolProfile?.primary_education?.internetadres}
                icon={<Website />}
              />
            </TitleInfoContainer>
            <TitleInfoContainer>
              <ProfileInfoTitle>{t('contact')}</ProfileInfoTitle>
              <ProfileInfoBlock
                label={t('emailShort')}
                value={schoolProfile?.contact_email}
                icon={<Email />}
              />
              <ProfileInfoBlock
                label={t('phone')}
                value={schoolProfile?.contact_phone}
                icon={<Phone />}
              />
            </TitleInfoContainer>
            <ProfileActions<Partial<School>>
              role={ROLES.SCHOOL}
              deleteRequest={requestAccountDeletion}
              updateProfile={updateProfile}
            />
          </SectionBigGap>
        </LoadingErrorHandler>
      </PageContent>
    </PageGrid>
  );
};
